/**
 * Imperial display conversions for the PNOĒ test detail that KEEP decimals.
 *
 * The shared `measurementUnitHelpers` / questionnaire `kgToPd`, `cmToIn`,
 * `inToFtAndIn`, `ftAndInToIn` all `Math.round` to whole lbs / inches, which
 * is what the questionnaire and Logic Builder want. The PNOĒ participant
 * panel stores metric with full precision, so a typed 165.4 lbs came back as
 * 165 and 5 ft 9.5 in as 5 ft 10 in. These variants are local to the PNOĒ
 * detail on purpose — the shared helpers keep their rounding for their own
 * callers.
 */

const LBS_PER_KG = 1 / 0.45359237; // exact inverse of the stored factor
const IN_PER_CM = 1 / 2.54;

const roundTo = (n: number, decimals: number) => Number(n.toFixed(decimals));

/** kg → lbs, one decimal by default (165.4). */
export const kgToLbsPrecise = (kg: number, decimals = 1): number =>
  roundTo(kg * LBS_PER_KG, decimals);

/** cm → total inches, unrounded (callers decide the precision). */
export const cmToInchesPrecise = (cm: number): number => cm * IN_PER_CM;

/**
 * Total inches → { feet, inches } with the inches kept to `decimals`
 * (5 ft 9.5 in). Rounding the remainder can produce 12.0, which carries into
 * the feet so the pair never shows "5 ft 12 in".
 */
export const inchesToFtAndInPrecise = (
  totalInches: number,
  decimals = 1
): { feet: number; inches: number } => {
  let feet = Math.floor(totalInches / 12);
  let inches = roundTo(totalInches - feet * 12, decimals);
  if (inches >= 12) {
    feet += 1;
    inches = 0;
  }
  return { feet, inches };
};

/** { feet, inches } → total inches, no rounding (5 ft 9.5 in → 69.5). */
export const ftAndInToInchesPrecise = (feet: number, inches: number): number =>
  feet * 12 + inches;
